import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

interface Race {
  id: string;
  name: string;
  date: any;
  location?: string;
  runnerCount?: number;
  voiceNoteCount?: number;
}

type FilterType = 'next30days' | 'allFuture' | 'all';

const InternalRacesPage: React.FC = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [races, setRaces] = useState<Race[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'name' | 'date'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState<FilterType>('next30days');

  const PASSWORD = 'owlplutowasp';

  // Check if already unlocked in localStorage
  useEffect(() => {
    const unlocked = localStorage.getItem('internal_races_unlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
  }, []);

  // Fetch races when unlocked
  useEffect(() => {
    if (isUnlocked) {
      fetchRaces();
    }
  }, [isUnlocked]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === PASSWORD) {
      setIsUnlocked(true);
      localStorage.setItem('internal_races_unlocked', 'true');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
      setPasswordInput('');
    }
  };

  const fetchRaces = async () => {
    setLoading(true);
    try {
      // Fetch all races
      const racesSnapshot = await getDocs(collection(db, 'races'));
      const racesData = await Promise.all(
        racesSnapshot.docs.map(async (doc) => {
          const raceData = doc.data();

          // Count runners for this race
          const runnersQuery = query(
            collection(db, 'runnerRaces'),
            where('raceId', '==', doc.id)
          );
          const runnersSnapshot = await getDocs(runnersQuery);
          const runnerCount = runnersSnapshot.size;

          // Count voice notes for this race
          const voiceNotesQuery = query(
            collection(db, 'voiceNotes'),
            where('raceId', '==', doc.id)
          );
          const voiceNotesSnapshot = await getDocs(voiceNotesQuery);
          const voiceNoteCount = voiceNotesSnapshot.size;

          return {
            id: doc.id,
            name: raceData.name || 'Unnamed Race',
            date: raceData.date,
            location: raceData.location || 'N/A',
            runnerCount,
            voiceNoteCount
          };
        })
      );

      setRaces(racesData);
    } catch (error) {
      console.error('Error fetching races:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSort = (field: 'name' | 'date') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Apply date filter
  const getFilteredByDate = (races: Race[]) => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    switch (filter) {
      case 'next30days':
        return races.filter(race => {
          if (!race.date) return false;
          const raceDate = race.date.toDate ? race.date.toDate() : new Date(race.date);
          return raceDate >= now && raceDate <= thirtyDaysFromNow;
        });

      case 'allFuture':
        return races.filter(race => {
          if (!race.date) return false;
          const raceDate = race.date.toDate ? race.date.toDate() : new Date(race.date);
          return raceDate >= now;
        });

      case 'all':
      default:
        return races;
    }
  };

  const filteredAndSortedRaces = getFilteredByDate(races)
    .filter(race =>
      race.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      race.location?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else {
        const dateA = a.date?.toDate ? a.date.toDate().getTime() : 0;
        const dateB = b.date?.toDate ? b.date.toDate().getTime() : 0;
        comparison = dateA - dateB;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Password screen
  if (!isUnlocked) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>🔒 Internal Access</h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Enter password"
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                marginBottom: '1rem',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            {passwordError && (
              <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '14px' }}>
                {passwordError}
              </p>
            )}
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Unlock
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Races dashboard
  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Rally - Internal Race Dashboard
        </h1>
        <p style={{ color: '#6b7280' }}>Manage and view all races</p>
      </div>

      {/* Filter Tabs */}
      <div style={{
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '0.5rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setFilter('next30days')}
          style={{
            padding: '0.75rem 1.5rem',
            background: filter === 'next30days' ? '#3b82f6' : 'transparent',
            color: filter === 'next30days' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: filter === 'next30days' ? '3px solid #3b82f6' : 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Next 30 Days
        </button>
        <button
          onClick={() => setFilter('allFuture')}
          style={{
            padding: '0.75rem 1.5rem',
            background: filter === 'allFuture' ? '#3b82f6' : 'transparent',
            color: filter === 'allFuture' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: filter === 'allFuture' ? '3px solid #3b82f6' : 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          All Future Races
        </button>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '0.75rem 1.5rem',
            background: filter === 'all' ? '#3b82f6' : 'transparent',
            color: filter === 'all' ? 'white' : '#6b7280',
            border: 'none',
            borderBottom: filter === 'all' ? '3px solid #3b82f6' : 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          All Races
        </button>
      </div>

      {/* Search and Actions */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search races..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={fetchRaces}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Refresh
        </button>
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '14px' }}>
        Showing {filteredAndSortedRaces.length} race{filteredAndSortedRaces.length !== 1 ? 's' : ''}
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          Loading races...
        </div>
      )}

      {/* Races table */}
      {!loading && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Race Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('date')}
                  style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontWeight: '600',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Location</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Runners</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Voice Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedRaces.map((race) => (
                <tr key={race.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '1rem', fontWeight: '500' }}>{race.name}</td>
                  <td style={{ padding: '1rem', color: '#6b7280' }}>{formatDate(race.date)}</td>
                  <td style={{ padding: '1rem', color: '#6b7280' }}>{race.location}</td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#3b82f6', fontWeight: '600' }}>
                    {race.runnerCount}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center', color: '#10b981', fontWeight: '600' }}>
                    {race.voiceNoteCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAndSortedRaces.length === 0 && (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              No races found for this filter
            </div>
          )}
        </div>
      )}

      {/* Summary stats - only for filtered results */}
      {!loading && filteredAndSortedRaces.length > 0 && (
        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
          <div style={{
            flex: 1,
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {filteredAndSortedRaces.length}
            </div>
            <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>Races</div>
          </div>
          <div style={{
            flex: 1,
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {filteredAndSortedRaces.reduce((sum, race) => sum + (race.runnerCount || 0), 0)}
            </div>
            <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>Total Runners</div>
          </div>
          <div style={{
            flex: 1,
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
              {filteredAndSortedRaces.reduce((sum, race) => sum + (race.voiceNoteCount || 0), 0)}
            </div>
            <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>Total Voice Notes</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalRacesPage;
